import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { UsersIcon } from 'lucide-react';
import { mockTeamMembers, Ticket } from '../data/mockData';
import { getUserTicketMetrics } from '../utils/ticketMetrics';
type TeamWorkloadProps = {
  tickets: Ticket[];
};
export function TeamWorkload({
  tickets
}: TeamWorkloadProps) {
  const teamMembers = mockTeamMembers.filter(member => member.name !== 'Unassigned').map(member => ({
    ...member,
    ...getUserTicketMetrics(tickets, member.name)
  }));
  const chartData = [...teamMembers].sort((firstMember, secondMember) => secondMember.activeTickets - firstMember.activeTickets);
  return <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 h-full flex flex-col" data-id="element-34" data-mp="2yizmamtqa86n">
      <div className="flex items-center mb-6" data-id="element-35" data-mp="2tk6acyu3ap8l">
        <UsersIcon className="w-5 h-5 text-blue-500 mr-2" data-id="element-36" data-mp="2pfymf3vghdbf" />
        <div data-id="element-37" data-mp="2piuh8jc0blvn">
          <h2 className="text-lg font-bold text-gray-900" data-id="element-38" data-mp="2fc3iwy7bk9uc">Team Operations</h2>
          <p className="mt-1 text-xs text-gray-500" data-id="element-39" data-mp="2fa0jxoo6v6dh">
            On Hold cases and hold time are excluded from workload and performance metrics.
          </p>
        </div>
      </div>

      <div className="flex-1 space-y-8" data-id="element-40" data-mp="25fqq02jlm00n">
        <div data-id="element-41" data-mp="2acqw2dgg72rr">
          <h3 className="text-sm font-semibold text-gray-700 mb-4" data-id="element-42" data-mp="2ach1eckgk7fq">
            Workload Distribution
          </h3>
          <div className="h-48 w-full" data-id="element-43" data-mp="2y3ml52tirjvb">
            <ResponsiveContainer width="100%" height="100%" data-id="element-44" data-mp="2y0ibhpxabvx9">
              <BarChart data={chartData} layout="vertical" margin={{
              top: 0,
              right: 20,
              left: 0,
              bottom: 0
            }} data-id="element-45" data-mp="330cznlpoe7em">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f3f4f6" data-id="element-46" data-mp="1dmzp3eqyzn52" />
                <XAxis type="number" hide data-id="element-47" data-mp="1hpnxxmfoou21" />
                <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#6b7280'
              }} width={80} data-id="element-48" data-mp="3kbjjukla2w1v" />
                <Tooltip cursor={{
                fill: '#f9fafb'
              }} contentStyle={{
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
              }} data-id="element-49" data-mp="35lccp10q0zgh" />
                <Bar dataKey="activeTickets" radius={[0, 4, 4, 0]} barSize={20} data-id="element-50" data-mp="2ro5x1fbangvh">
                  {chartData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.activeTickets > 10 ? '#ef4444' : entry.activeTickets > 5 ? '#3b82f6' : '#10b981'} data-id="element-51" data-mp="pvhmekwblr5o" data-mpi={index} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div data-id="element-52" data-mp="174tpkgjrv7u4">
          <h3 className="text-sm font-semibold text-gray-700 mb-4" data-id="element-53" data-mp="174jzksyq90of">
            Team Performance
          </h3>
          <div className="overflow-x-auto" data-id="element-54" data-mp="afcnscnwu5xp">
            <table className="w-full text-left text-sm" data-id="element-55" data-mp="age0elkkr0hj">
              <thead data-id="element-56" data-mp="egeeu67obbol">
                <tr className="text-gray-500 border-b border-gray-100" data-id="element-57" data-mp="efwhf699yrf3">
                  <th className="pb-3 font-medium" data-id="element-58" data-mp="jdy2txu6b2tq">Admin</th>
                  <th className="pb-3 font-medium text-center" data-id="element-59" data-mp="odj3qdswll93">Active</th>
                  <th className="pb-3 font-medium text-center" data-id="element-60" data-mp="obg1mveh81o0">Avg Resp</th>
                  <th className="pb-3 font-medium text-center" data-id="element-61" data-mp="1utcwarn6bck3">Avg Resolve</th>
                  <th className="pb-3 font-medium text-center" data-id="element-62" data-mp="1zrwnp7kjdgju">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-63" data-mp="38qfxa77zdn3o">
                {teamMembers.map(member => <tr key={member.id} data-id="element-64" data-mp="33sntaeu2aaj4">
                      <td className="py-3 font-medium text-gray-800" data-id="element-65" data-mp="3hqcddkgb1dn4">
                        {member.name}
                      </td>
                      <td className="py-3 text-center text-gray-600" data-id="element-66" data-mp="3cqq4tobf8lkn">
                        {member.activeTickets}
                      </td>
                      <td className="py-3 text-center text-gray-600" data-id="element-67" data-mp="3rmfkl4q0k2gx">
                        {Math.round(member.avgResponseTime)}m
                      </td>
                      <td className="py-3 text-center text-gray-600" data-id="element-68" data-mp="4aeknrs3ltln">
                        {member.avgResolutionTime.toFixed(1)}h
                      </td>
                      <td className="py-3 text-center" data-id="element-69" data-mp="e9cena8amfc1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${member.status === 'Available' ? 'bg-green-100 text-green-700' : member.status === 'Busy' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-70" data-mp="ea5fxvohgcms">
                          {member.status}
                        </span>
                      </td>
                    </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>;
}